import type {
    ActionResponse,
    Auth,
    LoanRequest,
    LoanRequestByDocumentsGroupsPane,
    LoanRequestOption
} from 'typings/portaro.be.types';
import type LoginService from '../../../login/login.service';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {IOnDestroy, IPromise, IScope, ITimeoutService} from 'angular';
import type LoanRequestService from '../../services/loan-request.service';
import bind from 'bind-decorator';
import type LoanRequestDataService from '../../data-services/loan-request.data-service';
import KpPluralizeText from '../../components/kp-pluralize-text/KpPluralizeText.svelte';
import KpUniversalForm from '../../value-editors/kp-universal-form/KpUniversalForm.svelte';
import type {FormController} from '../../value-editors/internal/forms/types';
import type {Subscription} from 'rxjs';
import KpUniversalFormForceSettingsWrapper
    from '../../value-editors/kp-value-editor-force-settings/KpUniversalFormForceSettingsWrapper.svelte';
import type {ForceSetting} from '../../value-editors/kp-value-editor-force-settings/types';
import {isEqual} from 'lodash';
import type {DateValueEditorValidations} from '../../value-editors/editors/date/types';
import KpValueEditor from '../../value-editors/kp-value-editor/KpValueEditor.svelte';

interface CapabilityNameToTemplateUrlMapping {
    cssClass: string;
    templateUrl: string;
}

interface UniversalFormConnector {
    svelteComponent: any;
    formController: FormController<any>;
    formSubscription: Subscription;
    formInvalid?: boolean;
}

type CapabilitiesWithForms = 'standardOrderability' | 'standardReservability' | 'standardVisitOrderability' | 'standardVisitReservability' | 'slotVisitOrderability';

// TODO: replace this template including with independent components
const CAPABILITY_NAME_TO_TEMPLATE_URL_MAPPING = {
    'pickupability': {
        cssClass: 'loan-request-option-pickup',
        templateUrl: require('./../loan-request-form/templates/pickupability.tpl.ftl')
    },
    'visitPickupability': {
        cssClass: 'loan-request-option-visit-pickup',
        templateUrl: require('../loan-request-form/templates/visit-pickupability.tpl.ftl')
    },
    'standardOrderability': {
        cssClass: 'loan-request-option-order',
        templateUrl: require('./../loan-request-form/templates/standard-orderability.tpl.ftl')
    },
    'standardVisitOrderability': {
        cssClass: 'loan-request-option-visit-order',
        templateUrl: require('./../loan-request-form/templates/standard-visit-orderability.tpl.ftl')
    },
    'slotVisitOrderability': {
        cssClass: 'loan-request-option-visit-order',
        templateUrl: require('./../loan-request-form/templates/slot-visit-orderability.tpl.ftl')
    },
    'standardReservability': {
        cssClass: 'loan-request-option-reservation',
        templateUrl: require('./../loan-request-form/templates/standard-reservability.tpl.ftl')
    },
    'standardVisitReservability': {
        cssClass: 'loan-request-option-visit-reservation',
        templateUrl: require('./../loan-request-form/templates/standard-visit-reservability.tpl.ftl')
    },
    'ereadingLoanability': {
        cssClass: 'loan-request-option-ereading',
        templateUrl: require('./../loan-request-form/templates/ereading-loanability.tpl.ftl')
    },
    'flexibooksLoanability': {
        cssClass: 'loan-request-option-flexibooks',
        templateUrl: require('./../loan-request-form/templates/flexibooks-loanability.tpl.ftl')
    },
    'levnaknihovnaLoanability': {
        cssClass: 'loan-request-option-levnaknihovna',
        templateUrl: require('./../loan-request-form/templates/levnaknihovna-loanability.tpl.ftl')
    },
    'mailRequestability': {
        cssClass: 'loan-request-option-email',
        templateUrl: require('./../loan-request-form/templates/mail-requestability.tpl.ftl')
    },
    'mvsRequestability': {
        cssClass: 'loan-request-option-mvs',
        templateUrl: require('./../loan-request-form/templates/mvs-requestability.tpl.ftl')
    },
    'downloadability': {
        cssClass: 'loan-request-option-download',
        templateUrl: require('./../loan-request-form/templates/downloadability.tpl.ftl')
    }
};

/**
 * @ngdoc controller
 * @name ModalLoanRequestFormController
 * @module portaro.conversation
 */
export default class ModalLoanRequestFormController implements IOnDestroy, Record<`${CapabilitiesWithForms}Form`, UniversalFormConnector> {
    public static controllerName = 'ModalLoanRequestFormController';

    public capabilityNameToTemplateUrlMapping: {[capabilityName: string]: CapabilityNameToTemplateUrlMapping} = CAPABILITY_NAME_TO_TEMPLATE_URL_MAPPING;
    // TODO: remove this after each capability gets its own component
    public standardOrderabilityForm: UniversalFormConnector = null;
    public standardReservabilityForm: UniversalFormConnector = null;
    public standardVisitOrderabilityForm: UniversalFormConnector = null;
    public standardVisitReservabilityForm: UniversalFormConnector = null;
    public slotVisitOrderabilityForm: UniversalFormConnector = null;


    public kpPluralizeTextComponent = KpPluralizeText;
    public kpUniversalFormComponent = KpUniversalForm;
    public kpUniversalFormForceSettingsWrapper = KpUniversalFormForceSettingsWrapper;
    public kpValueEditorComponent = KpValueEditor;
    public slotScopeDateRangeValueEditorValidations: DateValueEditorValidations = {
        minDate: new Date(new Date().setHours(0, 0, 0, 0)).toISOString()
    };
    public slotScopeDateRange = {
        fromDate: new Date(new Date().setHours(0, 0, 0, 0)).toISOString()
    };
    public kpUniversalFormForceSettings: ForceSetting[] = [
        {type: 'acceptable', options: {switchToInlineModeThreshold: 0, optionIdResolver: ({option}) => option.text}},
        {type: 'single-acceptable', options: {switchToInlineModeThreshold: 0, optionIdResolver: ({option}) => option.text}},
        {type: 'multiple-acceptable', options: {switchToInlineModeThreshold: 0, optionIdResolver: ({option}) => option.text}}
    ];
    private timeslotUniversalFormProps = {};

    private form: LoanRequestByDocumentsGroupsPane;
    private model: any;
    private formCancelFunction: () => void;
    private formSubmitFunction: (promise: Promise<any>) => any;
    private formCloseAndReopenFunction: (reopenPromise: Promise<void>) => void;
    private formReopenFunction: (reopenPromise: Promise<any>) => void;

    /*@ngInject*/
    constructor(private loanRequestService: LoanRequestService,
                private currentAuth: Auth,
                private loginService: LoginService,
                private loanRequestDataService: LoanRequestDataService,
                private $scope: IScope,
                private $timeout: ITimeoutService
    ) {
    }

    public init(
        model,
        formCancelFunction: () => void,
        formSubmitFunction: (promise: Promise<any>) => any,
        formCloseAndReopenFunction: (reopenPromise: Promise<void>) => void,
        formReopenFunction: (reopenPromise: Promise<any>) => void
    ) {
        this.form = model.form;
        this.model = model;
        this.formCancelFunction = formCancelFunction;
        this.formSubmitFunction = formSubmitFunction;
        this.formCloseAndReopenFunction = formCloseAndReopenFunction;
        this.formReopenFunction = formReopenFunction;
    }

    public $onDestroy() {
        this.standardOrderabilityForm?.formSubscription.unsubscribe();
        this.standardReservabilityForm?.formSubscription.unsubscribe();
        this.standardVisitOrderabilityForm?.formSubscription.unsubscribe();
        this.standardVisitReservabilityForm?.formSubscription.unsubscribe();
        this.slotVisitOrderabilityForm?.formSubscription.unsubscribe();
    }

    // eslint-disable-next-line @typescript-eslint/require-await
    @ngAsync()
    public async sendRequest(endpointPath: string, option, group) {
        if (option.capability.loginRequired && !this.currentAuth.evided) {
            this.closeAndReopenForm(this.loginService.login());
            return;
        }
        if (this.form.groups.length === 1) {
            this.submitForm(this.loanRequestService.requestLoan(endpointPath, option.formObject as LoanRequest));
        } else {
            this.reopenFormAfterSubmit(group, this.loanRequestService.requestLoan(endpointPath, option.formObject as LoanRequest));
        }
    }

    public isValidOrder(request): boolean {
        return request &&
            request.desiredBuilding !== null;
    }

    public isValidReservation(request): boolean {
        return request &&
            request.desiredBuildings.length > 0;
    }

    public showLoginFirstButton(option): boolean {
        return option.capability.loginRequired && !this.currentAuth.evided;
    }

    public closeAndReopenForm(reopen: Promise<any>) {
        this.formCloseAndReopenFunction(reopen);
    }

    public closeForm() {
        this.formCancelFunction();
    }

    @bind
    @ngAsync()
    public async loadSlotOrderingForm(option: LoanRequestOption) {
        const params = {
            // @ts-ignore believe it is there
            document: option.formObject.items[0].document.id,
            // @ts-ignore believe it is there
            requester: option.formObject.requester.id,
            // @ts-ignore believe it is there
            requestDepartment: option.formObject.desiredBuilding.id,
            // @ts-ignore believe it is there
            scopeDateRange: {
                fromDate: this.slotScopeDateRange.fromDate,
                toDate: new Date(new Date(this.slotScopeDateRange.fromDate).setHours(24, 0, 0, 0)).toISOString() // = slotScopeDateRange plus 1 day
            }
        };
        option.form = await this.loanRequestDataService.getDocumentCapabilityForm(option.capability.name, params);
        this.$timeout(() => this.reconnectToSlotVisitOrderabilityForm());
    }

    @bind
    public slotOrderingFormChange(option) {
        this.loadSlotOrderingForm(option);
    }

    public connectFormController(svelteComponent, formType: `${CapabilitiesWithForms}Form`) {
        const formController: FormController<any> = svelteComponent.getFormController();
        this[formType] = {svelteComponent, formController, formSubscription: null}; // we need form connector to already exist before calling subscribe
        this[formType].formSubscription = formController.getFieldState$()
            .subscribe((state) => {
                this[formType].formInvalid = state.invalid;
                this.$scope.$applyAsync(); // this is for angular to notice data changes (TODO: remove in svelte)
            });
    }

    // this is for angular to notice data changes (TODO: remove in svelte)
    public refreshScope() {
        this.$scope.$applyAsync();
    }

    // TODO: remove this method when refactoring to svelte
    // this is hack to make svelte-angular data binding work in angular
    // if new props object is created every time then it crashes angular due to the digest cycle loop
    // if props is const object and its properties are mutated then angular does not detect changes
    // => we need to detect changes ourself and cache most current props
    public getTimeslotFormProps = (newProps) => {
        if (!isEqual(this.timeslotUniversalFormProps, newProps)) {
            this.timeslotUniversalFormProps = newProps;
        }
        return this.timeslotUniversalFormProps;
    };

    private reconnectToSlotVisitOrderabilityForm() {
        this.slotVisitOrderabilityForm.formSubscription.unsubscribe();
        this.slotVisitOrderabilityForm.formController = this.slotVisitOrderabilityForm.svelteComponent.getFormController();
        this.slotVisitOrderabilityForm.formSubscription = this.slotVisitOrderabilityForm.formController.getFieldState$()
            .subscribe((state) => {
                this.slotVisitOrderabilityForm.formInvalid = state.invalid;
                this.$scope.$applyAsync(); // this is for angular to notice data changes (TODO: remove in svelte)
            });
    }

    private reopenFormAfterSubmit(group, reopen: IPromise<any>) {
        const promise = new Promise<any>((resolve) => {
            reopen
                .then((response) => {
                    group.responses = [response];
                    resolve(this.model);
                })
                .catch(() => resolve(this.model));
        });
        this.formReopenFunction(promise);
    }

    private submitForm(promise: Promise<ActionResponse>) {
        this.formSubmitFunction(promise);
    }
}
